import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal, Spin, message } from "antd";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useSonicProspectAccess } from "hooks/useSonicProspectAccess";
import SonicProspectLockPage from "./SonicProspectLockPage";
import BotList from "./BotList";
import BotWizard from "./BotWizard";
import ProductHome from "./ProductHome";
import ClaimedReady from "./ClaimedReady";
import BotDetail from "./BotDetail";
import BillingDrawer from "./BillingDrawer";
import {
  prospectHasLiveIntegration,
  setProspectSetupReady,
} from "util/prospectSetupGate";
import BotConversations from "./BotConversations";
import BotHandoffSettings from "./BotHandoffSettings";
import BotHandoffLiveConsole from "./BotHandoffLiveConsole";
import { BOT_STATUS, DEFAULT_BOT_DRAFT } from "./constants";
import { defaultAssistantName } from "./channelKind";
import { launchEmbeddedSignup } from "../Sonic/whatsappEmbeddedSignup";
import {
  acceptProspectMembership,
  checkFeedbackApiReady,
  connectProspectWhatsapp,
  createProspectBot,
  getProspectBot,
  isUsingLocalStore,
  listProspectBots,
  listProspectMemberships,
  startProspectEtsyOAuth,
  startProspectInstagramOAuth,
} from "./prospectService";
import styles from "../Sonic/style.module.scss";
import spStyles from "./style.module.scss";
import { trackProspectProductMilestone } from "util/prospectTrialAnalytics";
import { apiRequest } from "util/services";
import { confirmCalendarConnection } from "../SonicCalendar/calendarService";

const LEAVE_COPY = {
  instagram: {
    title: "sonic_prospect.ig_oauth_leave_title",
    body: "sonic_prospect.ig_oauth_leave_body",
    ok: "sonic_prospect.ig_oauth_leave_ok",
  },
  whatsapp: {
    title: "sonic_prospect.wa_leave_title",
    body: "sonic_prospect.wa_leave_body",
    ok: "sonic_prospect.wa_leave_ok",
  },
  shopify: {
    title: "sonic_prospect.shopify_leave_title",
    body: "sonic_prospect.shopify_leave_body",
    ok: "sonic_prospect.shopify_leave_ok",
  },
  etsy: {
    title: "sonic_prospect.etsy_leave_title",
    body: "sonic_prospect.etsy_leave_body",
    ok: "sonic_prospect.etsy_leave_ok",
  },
};

/**
 * Sonic Prospect — embeddable site chatbot builder.
 * Fixed nav entry; page-level entitlement gate only.
 */
const SonicProspect = ({ authenticated, user, theme }) => {
  const { t } = useTranslation();
  const { canAccess, loading: accessLoading } = useSonicProspectAccess(authenticated, user);
  const location = useLocation();
  const deepLinkHandled = useRef(false);
  const [bots, setBots] = useState([]);
  const [prospectLimits, setProspectLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingBot, setEditingBot] = useState(null);
  const [conversationsBot, setConversationsBot] = useState(null);
  const [conversationsInitialTab, setConversationsInitialTab] = useState("all");
  const [handoffBot, setHandoffBot] = useState(null);
  const [liveBot, setLiveBot] = useState(null);
  const [view, setView] = useState("home");
  const [memberships, setMemberships] = useState([]);
  const [surfaceMeta, setSurfaceMeta] = useState({});
  const [busyKey, setBusyKey] = useState("");
  const [leaveSurface, setLeaveSurface] = useState("");
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [selfServe, setSelfServe] = useState(null);
  const [obStep, setObStep] = useState(1);
  const [obProduct, setObProduct] = useState("channels");
  const [selectedBot, setSelectedBot] = useState(null);
  const [detailTab, setDetailTab] = useState("conversations");
  const [billingOpen, setBillingOpen] = useState(false);

  // The Calendar tab's "Raise the limit" opens Billing, where the monthly limit is set.
  useEffect(() => {
    const open = () => setBillingOpen(true);
    window.addEventListener("sonic-prospect-open-billing", open);
    return () => window.removeEventListener("sonic-prospect-open-billing", open);
  }, []);

  const fetchBots = useCallback(async (options = {}) => {
    if (!options.silent) setLoading(true);
    try {
      await checkFeedbackApiReady();
      const { bots: list, limits } = await listProspectBots();
      setBots(list);
      setProspectLimits(limits);
      try {
        const membership = await listProspectMemberships();
        setMemberships(membership.memberships || []);
        setSurfaceMeta(membership);
      } catch (_) {
        setMemberships([]);
      }
    } catch (_) {
      if (!options.silent) {
        setBots([]);
        setProspectLimits(null);
      }
    } finally {
      if (!options.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!accessLoading && canAccess) {
      fetchBots();
    }
  }, [accessLoading, canAccess, fetchBots]);

  useEffect(() => {
    if (accessLoading || !canAccess) return undefined;
    const refresh = () => {
      if (document.visibilityState === "visible") fetchBots({ silent: true });
    };
    document.addEventListener("visibilitychange", refresh);
    const timer = setInterval(refresh, 60000);
    return () => {
      document.removeEventListener("visibilitychange", refresh);
      clearInterval(timer);
    };
  }, [accessLoading, canAccess, fetchBots]);

  useEffect(() => {
    if (accessLoading || !canAccess) return undefined;
    let stop = false;
    apiRequest("subscriptions/sonic-prospect/self-serve/status", "POST", {}, true).then(
      (result) => {
        if (!stop && result) setSelfServe(result);
      }
    );
    return () => {
      stop = true;
    };
  }, [accessLoading, canAccess]);

  useEffect(() => {
    if (!selfServe?.requires_card || selfServe.card_saved) return;
    window.location.assign("/signup?product=sonic-prospect&lang=en");
  }, [selfServe]);

  useEffect(() => {
    if (loading || accessLoading || !canAccess) return;
    if (selfServe?.claimed) return;
    if (bots.length === 0) setOnboardingOpen(true);
  }, [loading, accessLoading, canAccess, bots.length, selfServe]);

  useEffect(() => {
    setProspectSetupReady(prospectHasLiveIntegration(bots));
    return () => setProspectSetupReady(false);
  }, [bots]);

  const openConversations = useCallback(async (bot, initialTab = "all") => {
    setLoading(true);
    try {
      const full = await getProspectBot(bot.id);
      setConversationsInitialTab(initialTab);
      setConversationsBot(full || bot);
      setView("conversations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (deepLinkHandled.current || accessLoading || !canAccess || loading) return;
    const params = new URLSearchParams(location.search);
    if (params.get("instagram") || params.get("hubspot")) return;
    const botId = params.get("bot");
    if (!botId) return;

    let cancelled = false;
    const tab = params.get("tab");
    const initialTab = tab === "insights" || tab === "calendar" ? tab : "all";

    (async () => {
      const listed = bots.find((b) => String(b.id) === String(botId));
      let bot = listed;
      if (!bot) {
        try {
          bot = await getProspectBot(botId);
        } catch (_) {
          bot = null;
        }
      }
      if (cancelled || !bot) return;
      deepLinkHandled.current = true;
      setSelectedBot(bot);
      setDetailTab(initialTab === "all" ? "conversations" : initialTab);
      window.history.replaceState({}, "", "/console/sonic-prospect");
    })();

    return () => {
      cancelled = true;
    };
  }, [accessLoading, canAccess, loading, bots, location.search]);

  useEffect(() => {
    if (accessLoading || !canAccess) return;
    const params = new URLSearchParams(location.search);
    const ig = params.get("instagram");
    if (!ig) return;
    if (ig === "connected") message.success(t("sonic_prospect.ig_oauth_done"));
    else if (ig === "in_use" || ig === "already_connected") message.error(t("sonic_prospect.ig_in_use"));
    else if (ig === "no_instagram_account") message.error(t("sonic_prospect.ig_oauth_no_account"));
    else if (ig === "membership") message.warning(t("sonic_prospect.ig_oauth_membership"));
    else message.error(t("sonic_prospect.ig_connect_error"));
    window.history.replaceState({}, "", "/console/sonic-prospect");
    fetchBots();
  }, [accessLoading, canAccess, location.search, t, fetchBots]);

  useEffect(() => {
    if (accessLoading || !canAccess) return;
    // Sonic Calendar OAuth return: ?bot=<id>&tab=calendar&calendar=connected|declined|scopes|error.
    // The bot deep link above opens the Calendar tab; this only reports the outcome.
    // "confirm" carries a one-time code: the signed-in user finishes the connection they started.
    const params = new URLSearchParams(location.search);
    const result = params.get("calendar");
    if (!result) return;
    if (result === "confirm") {
      const botId = params.get("bot");
      const code = params.get("calendar_code");
      confirmCalendarConnection(botId, code)
        .then(() => {
          message.success(t("sonic_calendar.connect_done"));
          window.dispatchEvent(new Event("sonic-calendar-connected"));
        })
        .catch(() => message.error(t("sonic_calendar.connect_error")));
    } else if (result === "declined") message.info(t("sonic_calendar.connect_declined"));
    else if (result === "admin") message.warning(t("sonic_calendar.connect_admin"));
    else if (result === "scopes") message.error(t("sonic_calendar.connect_scopes"));
    else message.error(t("sonic_calendar.connect_error"));
  }, [accessLoading, canAccess, location.search, t]);

  useEffect(() => {
    if (accessLoading || !canAccess) return;
    const params = new URLSearchParams(location.search);
    const hubspot = params.get("hubspot");
    if (!hubspot) return;
    if (hubspot === "connected") message.success(t("sonic_prospect.hubspot_done"));
    else message.error(t("sonic_prospect.hubspot_error"));
    window.history.replaceState({}, "", "/console/sonic-prospect");
  }, [accessLoading, canAccess, location.search, t]);

  const openCreate = () => {
    setEditingBot({ ...DEFAULT_BOT_DRAFT });
    setView("wizard");
  };

  const openEdit = async (bot) => {
    setLoading(true);
    try {
      const full = await getProspectBot(bot.id);
      setEditingBot(full || bot);
      setView("wizard");
    } finally {
      setLoading(false);
    }
  };

  const openInsights = (bot) => openConversations(bot, "insights");

  const openHandoff = async (bot) => {
    setLoading(true);
    try {
      const full = await getProspectBot(bot.id);
      setHandoffBot(full || bot);
      setView("handoff");
    } finally {
      setLoading(false);
    }
  };

  const openLive = async (bot) => {
    setLoading(true);
    try {
      const full = await getProspectBot(bot.id);
      setLiveBot(full || bot);
      setView("live");
    } finally {
      setLoading(false);
    }
  };

  const handleSaved = (saved) => {
    setEditingBot(saved);
    fetchBots();
    if (saved?.id) {
      trackProspectProductMilestone("first_bot_created", { bot_id: saved.id });
    }
  };

  const handleHandoffSaved = (saved) => {
    setHandoffBot(saved);
    fetchBots();
  };

  const handleAccept = async (productKey) => {
    setBusyKey(productKey);
    try {
      await acceptProspectMembership(productKey);
      const membership = await listProspectMemberships();
      setMemberships(membership.memberships || []);
      setSurfaceMeta(membership);
    } finally {
      setBusyKey("");
    }
  };

  const redirectToInstagram = async () => {
    setBusyKey("instagram");
    try {
      let bot = bots.find((item) => item.status === BOT_STATUS.ACTIVE);
      if (!bot) {
        bot = await createProspectBot({
          name: defaultAssistantName(user),
          status: BOT_STATUS.ACTIVE,
        });
      }
      const started = await startProspectInstagramOAuth(bot.id);
      if (started?.authorize_url) window.location.assign(started.authorize_url);
      else message.warning(t("sonic_prospect.ig_oauth_unconfigured"));
    } catch (_) {
      message.error(t("sonic_prospect.ig_connect_error"));
    } finally {
      setBusyKey("");
      setLeaveSurface("");
    }
  };

  const startWhatsApp = async () => {
    setBusyKey("whatsapp");
    try {
      const cfg = surfaceMeta.whatsapp || {};
      let bot = bots.find((item) => item.status === BOT_STATUS.ACTIVE) || bots[0];
      if (!bot) {
        bot = await createProspectBot({
          name: defaultAssistantName(user),
          status: BOT_STATUS.ACTIVE,
        });
      }
      let session;
      try {
        session = await launchEmbeddedSignup({
          appId: cfg.app_id,
          configId: cfg.config_id,
          graphVersion: cfg.graph_version,
        });
      } catch (err) {
        if (err?.message !== "cancelled") {
          message.error(t("sonic_prospect.wa_connect_error"));
        }
        return;
      }
      if (!session?.phone_number_id) {
        message.error(t("sonic_prospect.wa_connect_error"));
        return;
      }
      await connectProspectWhatsapp(bot.id, {
        code: session.code,
        phoneNumberId: session.phone_number_id,
        wabaId: session.waba_id || "",
      });
      message.success(t("sonic_prospect.wa_connected"));
      await fetchBots();
    } finally {
      setBusyKey("");
      setLeaveSurface("");
    }
  };

  const startEtsy = async () => {
    setBusyKey("etsy");
    try {
      const started = await startProspectEtsyOAuth();
      if (started?.authorize_url) window.location.assign(started.authorize_url);
      else message.warning(t("sonic_prospect.etsy_unconfigured"));
    } catch (_) {
      message.warning(t("sonic_prospect.etsy_unconfigured"));
    } finally {
      setBusyKey("");
      setLeaveSurface("");
    }
  };

  const continueLeave = () => {
    if (leaveSurface === "instagram") return redirectToInstagram();
    if (leaveSurface === "whatsapp") return startWhatsApp();
    if (leaveSurface === "etsy") return startEtsy();
    if (leaveSurface === "shopify" && surfaceMeta.shopify_install_url) {
      window.location.assign(surfaceMeta.shopify_install_url);
    }
  };

  const handleStart = async (productKey, surface) => {
    if (surface === "shopify") {
      if (!surfaceMeta.shopify_install_url) {
        message.warning(t("sonic_prospect.shopify_unconfigured"));
        return;
      }
      setLeaveSurface("shopify");
      return;
    }
    if (surface === "whatsapp") {
      const cfg = surfaceMeta.whatsapp || {};
      if (!cfg.configured || !cfg.app_id) {
        message.warning(t("sonic_prospect.wa_unconfigured"));
        return;
      }
      setLeaveSurface("whatsapp");
      return;
    }
    if (surface === "instagram" || surface === "etsy") {
      setLeaveSurface(surface);
      return;
    }
    setBusyKey(surface);
    try {
      if (surface === "website") {
        let bot = bots[0];
        if (!bot) {
          bot = await createProspectBot({
            name: selfServe?.brand_name || defaultAssistantName(user),
            websiteUrl: selfServe?.import_website || "",
            status: BOT_STATUS.DRAFT,
          });
        }
        setSelectedBot(bot);
        setObStep(3);
      }
    } finally {
      setBusyKey("");
    }
  };

  const backToList = () => {
    setView("home");
    setEditingBot(null);
    setConversationsBot(null);
    setConversationsInitialTab("all");
    setHandoffBot(null);
    setLiveBot(null);
    fetchBots();
  };

  if (accessLoading) {
    return (
      <div className={styles.sonicLockSpinner}>
        <Spin size="large" />
      </div>
    );
  }

  if (!canAccess) {
    return <SonicProspectLockPage theme={theme} />;
  }

  if (view === "conversations" && conversationsBot) {
    return (
      <BotConversations
        bot={conversationsBot}
        onBack={backToList}
        prospectLimits={prospectLimits}
        initialTab={conversationsInitialTab}
      />
    );
  }

  if (view === "live" && liveBot) {
    return (
      <BotHandoffLiveConsole
        bot={liveBot}
        onBack={backToList}
        userId={user?.uid || ""}
      />
    );
  }

  if (view === "handoff" && handoffBot) {
    return (
      <BotHandoffSettings
        bot={handoffBot}
        onBack={backToList}
        onSaved={handleHandoffSaved}
        onOpenLive={() => openLive(handoffBot)}
      />
    );
  }

  if (view === "wizard" && editingBot) {
    return (
      <BotWizard
        bot={editingBot}
        onBack={backToList}
        onSaved={handleSaved}
        onDeleted={backToList}
        prospectLimits={prospectLimits}
        whatsappConfig={surfaceMeta.whatsapp}
      />
    );
  }

  const closableOnboarding = bots.length > 0;
  const finishOnboarding = () => {
    setOnboardingOpen(false);
    fetchBots({ silent: true });
    if (selectedBot) setDetailTab("conversations");
  };

  const shell = selectedBot ? (
    <BotDetail
      bot={selectedBot}
      tab={detailTab}
      onTab={setDetailTab}
      onBack={() => setSelectedBot(null)}
      onSaved={(saved) => {
        setSelectedBot(saved || selectedBot);
        fetchBots({ silent: true });
      }}
      onDeleted={() => {
        setSelectedBot(null);
        fetchBots({ silent: true });
      }}
      prospectLimits={prospectLimits}
      userId={user?.uid || ""}
      whatsappConfig={surfaceMeta.whatsapp}
    />
  ) : (
    <BotList
      bots={bots}
      loading={loading}
      onOpen={(bot, tab) => {
        setSelectedBot(bot);
        setDetailTab(tab || "conversations");
      }}
      onAddChannel={() => {
        setObStep(1);
        setOnboardingOpen(true);
      }}
      onOpenBilling={() => setBillingOpen(true)}
      onRefresh={() => fetchBots({ silent: true })}
      onAssistantUpdated={(saved) => {
        if (saved?.id) {
          setBots((prev) => prev.map((bot) => (bot.id === saved.id ? { ...bot, ...saved } : bot)));
        }
      }}
      whatsappConfig={surfaceMeta.whatsapp}
      user={user}
    />
  );

  return (
    <>
      {selfServe?.payment_failed ? (
        <p className={spStyles.capBanner}>
          {selfServe.lead_capture_paused
            ? "Payment failed. Lead capture is paused until the card is updated."
            : "Payment failed. Lead capture stays on during the grace period. Update the card in billing."}
        </p>
      ) : null}
      {selfServe?.claimed && !selfServe.widget_installed ? (
        <ClaimedReady
          brandName={selfServe.brand_name || "your"}
          bot={bots[0]}
          onInstagram={() => setLeaveSurface("instagram")}
          onWhatsapp={() => setLeaveSurface("whatsapp")}
        />
      ) : null}
      {shell}
      <Modal
        open={onboardingOpen}
        onCancel={() => {
          if (closableOnboarding) setOnboardingOpen(false);
        }}
        closable={closableOnboarding}
        maskClosable={closableOnboarding}
        keyboard={closableOnboarding}
        footer={null}
        width={760}
        centered
        destroyOnClose={false}
        className={spStyles.onboardModal}
        styles={{
          mask: { background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" },
          content: {
            background: "#13131F",
            border: "1px solid rgba(139, 92, 246, 0.3)",
            borderRadius: 16,
            maxHeight: "85vh",
            overflow: "auto",
            padding: 32,
          },
        }}
      >
        <ProductHome
          step={obStep}
          productKey={obProduct}
          memberships={memberships}
          onAccept={handleAccept}
          onAdvance={(key) => {
            setObProduct(key);
            setObStep(2);
          }}
          onStart={handleStart}
          onDone={finishOnboarding}
          busyKey={busyKey}
        />
      </Modal>
      <BillingDrawer
        open={billingOpen}
        onClose={() => setBillingOpen(false)}
        bots={bots}
      />
      <Modal
        open={!!leaveSurface}
        title={leaveSurface ? t(LEAVE_COPY[leaveSurface].title) : ""}
        onCancel={() => setLeaveSurface("")}
        footer={[
          <Button key="cancel" onClick={() => setLeaveSurface("")}>
            {t("sonic_prospect.confirm_cancel")}
          </Button>,
          <Button
            key="go"
            type="primary"
            loading={busyKey === leaveSurface}
            onClick={continueLeave}
          >
            {leaveSurface ? t(LEAVE_COPY[leaveSurface].ok) : ""}
          </Button>,
        ]}
      >
        {leaveSurface ? t(LEAVE_COPY[leaveSurface].body) : ""}
      </Modal>
    </>
  );
};

export default SonicProspect;

export { isUsingLocalStore };
